import type {
  AccessGrant,
  BulkUserGrantOperation,
  EnrichedUserPermissionInfo,
} from '@/hooks/permissions/types'

const INVITE_ONLY_SELF_GRANT: AccessGrant = 'user_write'

export const includeCurrentUserForInviteOnly = (
  userIds: string[],
  currentUserId: string | undefined,
  isUnrestricted: boolean,
): string[] => {
  if (isUnrestricted || !currentUserId || userIds.includes(currentUserId)) {
    return userIds
  }

  return [currentUserId, ...userIds]
}

interface IncludeCurrentUserGrantForInviteOnlyArgs {
  isSwitchingToInviteOnly: boolean
  currentUserId: string | undefined
  users: Pick<
    EnrichedUserPermissionInfo,
    'userId' | 'currentUserGrant' | 'availableGrantsRestricted'
  >[]
  operations: BulkUserGrantOperation[]
}

export const includeCurrentUserGrantForInviteOnly = ({
  isSwitchingToInviteOnly,
  currentUserId,
  users,
  operations,
}: IncludeCurrentUserGrantForInviteOnlyArgs): BulkUserGrantOperation[] => {
  if (!isSwitchingToInviteOnly || !currentUserId) {
    return operations
  }

  const hasPendingCurrentUserOperation = operations.some(
    (operation) => operation.user_id === currentUserId,
  )
  if (hasPendingCurrentUserOperation) {
    return operations
  }

  const currentUser = users.find((user) => user.userId === currentUserId)
  if (
    !currentUser ||
    currentUser.currentUserGrant ||
    !currentUser.availableGrantsRestricted.includes(INVITE_ONLY_SELF_GRANT)
  ) {
    return operations
  }

  return [
    {
      user_id: currentUserId,
      grant: INVITE_ONLY_SELF_GRANT,
    },
    ...operations,
  ]
}
